/**
 * Created by Ning Jiang on 2021-05-05.
 */

import {DDayGameAssets} from "../DDayGameAssets";
import {DDay} from "../DDay";
import {DDayLayers, Layers} from "./DDayLayers";
import {BlitzKriegGameMode} from "../server/BlitzKriegGameMode";
import {TimelineLite, SteppedEase, gsap, Power2} from "gsap";
import {GameHelper} from "../../core/utils/GameHelper";
import {LoopableList} from "../../core/utils/LoopableList";

export class DDayBackground extends PIXI.Container {

    private _backgroundMain: PIXI.Sprite;
    private _xBetBackground: PIXI.spine.Spine;
    private _backgroundEffect: PIXI.spine.Spine;
    public backgroundEffectAnimation: LoopableList<string> = new LoopableList<string>(["FS_bgFX", "FS_bgFX2", "FS_bgFX3", "FS_bgFX4", "FS_bgFX5"]).setRandomizeAtWrapAround(true);
    public currentAnimName: string = "";
    private _enableEffect: boolean = false;
    private _introActiveFeaturesTextList: PIXI.spine.Spine[] = [];
    private _introFeaturesListContainer: PIXI.Container;

    constructor() {
        super();
        this.init();
    }

    private init() {
        this._backgroundMain = new PIXI.Sprite(DDay.assetLoader.getTextures(DDayGameAssets.MAIN_GAME_BACKGROUND)[0]);
        this._backgroundMain.position.set(360, 360);
        this._backgroundMain.anchor.set(0.5, 0.5);

        this._backgroundEffect = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("freespinBGEffects"));
        this._backgroundEffect.position.set(360, 360);
        this._backgroundEffect.alpha = 0;

        this._xBetBackground = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("xBetBackground"));
        this._xBetBackground.position.set(360, 360);
        this._xBetBackground.alpha = 0;

        this._backgroundEffect.state.onComplete = () => {
            let animName: string = this.backgroundEffectAnimation.next();
            if (animName === this.currentAnimName) {
                animName = this.backgroundEffectAnimation.next();
            }
            this.currentAnimName = animName;
            this._backgroundEffect.state.setAnimation(0, animName, false);

        }
        this._backgroundEffect.state.setAnimation(0, "FS_bgFX", false);

        this._introFeaturesListContainer = new PIXI.Container();

        DDayLayers.getLayer(Layers.BACKGROUND)?.addChild(this._backgroundMain, this._xBetBackground, this._introFeaturesListContainer, this._backgroundEffect);

        this.onOrientationChanged();
        this.onResize();
    }

    public enabledFeaturesTextList(): void {
        let atomicWild: PIXI.spine.Spine = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("introAtomicWildList"));
        let stuka: PIXI.spine.Spine = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("introStukaList"));
        let bomber: PIXI.spine.Spine = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("introBomberList"));
        let panzer: PIXI.spine.Spine = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("introPanzerList"));
        let howitzer: PIXI.spine.Spine = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("introHowitzerList"));

        atomicWild.position.set(360, 360);
        stuka.position.set(360, 360);
        bomber.position.set(360, 360);
        panzer.position.set(360, 360);
        howitzer.position.set(360, 360);

        atomicWild.name = "atomic_wildBasegame"
        stuka.name = "stuka_basegame";
        bomber.name = "bomber_basegame";
        panzer.name = "panzer_basegame";
        howitzer.name = "howitzer_basegame";

        atomicWild.alpha = 0;
        stuka.alpha = 0;
        bomber.alpha = 0;
        panzer.alpha = 0;
        howitzer.alpha = 0;

        this._introActiveFeaturesTextList.push(stuka, panzer, bomber, howitzer, atomicWild);
    }

    public changeBackground(mode: BlitzKriegGameMode, fromXBet: boolean = false): void {
        let texture: string = DDayGameAssets.MAIN_GAME_BACKGROUND;
        let isMainBackground: boolean = false;
        let effectAlpha: number = 0;
        switch (mode) {
            case  BlitzKriegGameMode.NORMAL:
                texture = DDayGameAssets.MAIN_GAME_BACKGROUND;

                isMainBackground = true;
                break;
            case BlitzKriegGameMode.FREESPIN:
                effectAlpha = 1;
                texture = DDayGameAssets.FREESPIN_GAME_BACKGROUND;
                isMainBackground = false;
                break;
            case BlitzKriegGameMode.SUPER_FREESPIN:
                effectAlpha = 1;
                texture = DDayGameAssets.SUPER_FREESPIN_GAME_BACKGROUND;
                isMainBackground = false;
                break;
        }
        if (isMainBackground) {
            this.enabledFeaturesTextList();
            this._introActiveFeaturesTextList.forEach((features: PIXI.spine.Spine) => {
                this._introFeaturesListContainer.addChild(features);
            });
        } else {
            this._introFeaturesListContainer.removeChildren();
            this._introActiveFeaturesTextList = [];
        }
        if (fromXBet) {

            let durr = this._xBetBackground.spineData.findAnimation(fromXBet ? "xbet_fadein" : "xbet_fadeout").duration;
            this._xBetBackground.alpha = 1;
            this._xBetBackground.state.setAnimation(0, fromXBet ? "xbet_fadein" : "xbet_fadeout", false);

        } else {
            this._xBetBackground.alpha = 0;
            this._backgroundMain.texture = DDay.assetLoader.getTextures(texture)[0];
        }
        this._enableEffect = effectAlpha === 1 ? true : false;
        this._backgroundEffect.alpha = effectAlpha;
    }

    public enableTextEffect(featureIdx: number): void {
        if (DDay.model.gameData.mode === BlitzKriegGameMode.NORMAL) {
            if (this._introActiveFeaturesTextList.length === 0) {
                this.enabledFeaturesTextList();
                this._introActiveFeaturesTextList.forEach((features: PIXI.spine.Spine) => {
                    this._introFeaturesListContainer.addChild(features);
                });
            }
            let feature: PIXI.spine.Spine = this._introActiveFeaturesTextList[featureIdx];
            feature.state.setAnimation(0, feature.name, true);
            feature.alpha = 1;
            feature.visible = true;
        }
    }

    public resetTextEffect(): void {
        this._introActiveFeaturesTextList.forEach((feature: PIXI.spine.Spine) => {
            feature.alpha = 0;
            GameHelper.resetSpine(feature, false);
        });
    }

    public fadeOutBackground(alphaVal: number = 1, duration: number = 0.2): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.to(this._backgroundMain, {
            alpha: alphaVal,
            duration: duration,
            ease: Power2.easeOut
        });
        return tl;
    }

    public playBackgroundFadeAnimation(from: gsap.TweenVars, to: gsap.TweenVars) {
        let tl: TimelineLite = new TimelineLite();
        tl.fromTo(this._backgroundMain, from, to);
        return tl;
    }

    public playBackgroundScaleAnimation(from: gsap.TweenVars, to: gsap.TweenVars) {
        let tl: TimelineLite = new TimelineLite();
        tl.fromTo(this._backgroundMain.scale, from, to);
        return tl;
    }

    public enableDisableEffect(enable: boolean): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.to(this, {duration: 0.1});
        if (this._enableEffect) {
            tl.to(this._backgroundEffect, {
                alpha: enable ? 0 : 1,
                duration: 0.2
            });
        }
        return tl;
    }


    public onOrientationChanged() {
        // TODO: check restore, mode, orientation
    }

    public onResize() {
        // TODO: check restore, mode, orientation
    }
}
